import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { StandardFields, FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, chainColor,
	FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'
import { qty, qtyUnit, productTypeOptions, productTypeLabel, ProductNameCell, FOOT_EXTRA_STYLE, relativeStats } from './chainStockUi'

// Что показывает ячейка матрицы: количество, себестоимость или норма на 100 чеков
const CELL_MODES = ['quantity', 'cost', 'per_100']

function ElementSales() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [cellMode, setCellMode] = useState('quantity')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)

	const filters = useChainFilters({ category_ids: [], product_type_ids: [], order_type_ids: [], source_ids: [] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions(['sources', 'categories'])

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/element-sales', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const branchById = useMemo(() => Object.fromEntries(branches.map(item => [item.pos_id, item])), [branches])

	function cellValue(row, posId) {
		const cell = row.cells?.[posId]
		if (!cell) return null
		if (cellMode === 'cost') return cell.cost
		if (cellMode === 'per_100') return cell.per_100
		return cell.quantity
	}

	function totalValue(row) {
		if (cellMode === 'cost') return row.cost
		if (cellMode === 'per_100') return row.per_100
		return row.quantity
	}

	function formatCell(value) {
		if (value === null || value === undefined) return '—'
		return cellMode === 'cost' ? money(value) : qty(value)
	}

	const rows = useMemo(() => {
		const list = data?.rows || []
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(row => row.product_name.toLowerCase().includes(query) || (row.category_name || '').toLowerCase().includes(query))
	}, [data, search])

	const readSortValue = (row, key) => {
		if (key.startsWith('pos:')) return cellValue(row, Number(key.slice(4)))
		if (key === 'total') return totalValue(row)
		if (key === 'share') return row.cost
		return row[key]
	}
	const { sorted, sort, toggle } = useSort(rows, 'cost', 'desc', readSortValue)

	const branchStats = useMemo(() => {
		const active = branches.filter(item => item.cheques > 0)
		return {
			cost_per_cheque: columnStats(active, 'cost_per_cheque'),
			cost_percent: columnStats(active, 'cost_percent'),
		}
	}, [branches])
	const maxBranchCost = useMemo(() => Math.max(0, ...branches.map(item => item.cost)), [branches])
	const maxRowCost = useMemo(() => Math.max(0, ...(data?.rows || []).map(row => row.cost)), [data])
	const perCheque = useMemo(() => branches.filter(item => item.cheques > 0 && item.cost_per_cheque !== null), [branches])
	const leanest = perCheque.length > 1 ? [...perCheque].sort((a, b) => a.cost_per_cheque - b.cost_per_cheque)[0] : null
	const heaviest = perCheque.length > 1 ? [...perCheque].sort((a, b) => b.cost_per_cheque - a.cost_per_cheque)[0] : null

	async function openDetails(row, posId) {
		const response = await GET(buildAnalyticsUrl('/chain/report/element-sales-cheques', applied, {
			product_id: row.product_id,
			pos_id: posId,
		}), {}, { loader: true })
		if (httpOk(response)) {
			setDetails({ ...response.data, row, pos_id: posId })
		}
	}

	const orderTypeLabels = { 1: t('hall'), 2: t('takeaway'), 3: t('delivery') }

	function kindLabel(item) {
		if (item.kind === 'return') return t('chain_cons_kind_return')
		if (item.reopened) return t('chain_cons_kind_reopened')
		return ''
	}

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const all = data.rows || []
		const productColumns = [
			{ label: t('name2'), type: 'text', width: 34 },
			{ label: t('unit'), type: 'text', width: 8 },
			{ label: t('category'), type: 'text', width: 20 },
			{ label: t('type'), type: 'text', width: 12 },
		]
		const productCells = row => [row.product_name, row.unit_name || '', row.category_name || '', productTypeLabel(t, row.product_type_id)]
		const matrix = (name, key, type, footer) => ({
			name,
			title: `${t('chain_cons_title')} — ${name}`,
			subtitle: period,
			columns: [...productColumns, ...branches.map(branch => ({ label: branch.pos_name, type })), { label: t('total'), type }],
			rows: all.map(row => [...productCells(row), ...branches.map(branch => row.cells?.[branch.pos_id]?.[key] ?? ''), row[key]]),
			footer,
		})

		downloadExcel(`${t('chain_cons_title')} ${periodText(data.start_date, data.end_date)}`, [
			matrix(t('quantity'), 'quantity', 'number'),
			matrix(t('cost_price'), 'cost', 'money', [t('total'), '', '', '', ...branches.map(branch => branch.cost), totals.cost]),
			matrix(t('chain_cons_per_100'), 'per_100', 'number'),
			{
				name: t('chain_branches'),
				title: `${t('chain_cons_title')} — ${t('chain_branches')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('cost_price'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: t('cheques'), type: 'int', width: 10 },
					{ label: t('chain_cons_cost_per_cheque'), type: 'money' },
					{ label: t('revenue'), type: 'money' },
					{ label: t('chain_percent_of_revenue'), type: 'percent', width: 14 },
					{ label: t('chain_incomes_positions'), type: 'int', width: 11 },
				],
				rows: [...branches].sort((a, b) => b.cost - a.cost).map(branch => [
					branch.pos_name, branch.cost, branch.share, branch.cheques, branch.cost_per_cheque, branch.revenue, branch.cost_percent, branch.positions,
				]),
				footer: [t('total'), totals.cost, 100, totals.cheques, totals.cost_per_cheque, totals.revenue, totals.cost_percent, totals.positions],
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_cons_title')} — ${details.row.product_name} — ${details.pos_name}`, [{
			name: t('cheques'),
			title: `${details.row.product_name} — ${details.pos_name}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('cheque'), type: 'text', width: 12 },
				{ label: t('order_type'), type: 'text', width: 12 },
				{ label: t('chain_cons_cheque_amount'), type: 'money' },
				{ label: t('quantity'), type: 'number' },
				{ label: t('cost_price'), type: 'money' },
				{ label: t('note'), type: 'text', width: 24 },
			],
			rows: details.rows.map(item => [
				formatDate(item.moved_at, 'dd.MM.yyyy HH:mm'), item.cheque_number ? `№ ${item.cheque_number}` : `#${item.cheque_id || ''}`,
				orderTypeLabels[item.order_type_id] || '', item.cheque_amount, item.quantity, item.cost, kindLabel(item),
			]),
			footer: [t('total'), '', '', '', details.quantity, details.cost, ''],
		}])
	}

	const modeLabels = {
		quantity: t('quantity'),
		cost: t('cost_price'),
		per_100: t('chain_cons_per_100'),
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_cons_title')}
				icon="uil-utensils-alt"
				filters={filters}
				wide
				onExcel={exportExcel}
				excelDisabled={!data}
			>
				<StandardFields filters={filters} options={options} branch={false} />
				<FilterField label={t('categories')}>
					<FilterSelect isMulti options={options.categories} allLabel={t('chain_all_categories')} value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label={t('chain_product_type')}>
					<FilterSelect isMulti options={productTypeOptions(t)} allLabel={t('chain_all_types')} value={draft.product_type_ids} onChange={(ids) => patch({ product_type_ids: ids })} />
				</FilterField>
			</ChainFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_cons_total_cost')} icon="uil-utensils-alt" color="#f06548"
						value={money(totals.cost)}
						sub={<>{percent(totals.cost_percent)} {t('chain_of_revenue')} · {formatMoney(totals.positions)} {t('chain_stock_positions_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_cons_cost_per_cheque')} icon="uil-receipt" color="#5b73e8"
						value={totals.cost_per_cheque !== null && totals.cost_per_cheque !== undefined ? money(totals.cost_per_cheque) : '—'}
						sub={<>{formatMoney(totals.cheques)} {t('chain_cons_cheques_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_cons_leanest')} icon="uil-arrow-down-right" color="#12b886"
						value={leanest ? leanest.pos_name : '—'}
						sub={leanest ? <>{money(leanest.cost_per_cheque)} {t('chain_cons_per_cheque_lower')} · {percent(leanest.cost_percent)} {t('chain_of_revenue')}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_cons_heaviest')} icon="uil-arrow-up-right" color="#f59f00"
						value={heaviest ? heaviest.pos_name : '—'}
						sub={heaviest ? <>{money(heaviest.cost_per_cheque)} {t('chain_cons_per_cheque_lower')} · {percent(heaviest.cost_percent)} {t('chain_of_revenue')}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_cons_by_products')}</h5>
							<FilterSegment
								options={CELL_MODES.map(id => ({ id, label: modeLabels[id] }))}
								value={cellMode}
								onChange={setCellMode}
							/>
						</div>
						<div className="chain-toolbar-group">
							{cellMode === 'per_100'
								? <HeatLegend low={t('chain_cons_more_usage')} high={t('chain_cons_less_usage')} />
								: <HeatLegend mode="success" />}
							<TableSearch value={search} onChange={setSearch} placeholder={t('chain_search_product')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="product_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('name2')}</SortTh>
									{branches.map((branch, index) => (
										<SortTh key={branch.pos_id} sortKey={`pos:${branch.pos_id}`} sort={sort} onSort={toggle} className="text-end"
											title={`${formatMoney(branch.cheques)} ${t('chain_cons_cheques_lower')}`}>
											<span className="chain-dot" style={{ background: chainColor(index) }}></span>
											{branch.pos_name}
										</SortTh>
									))}
									<SortTh sortKey="total" sort={sort} onSort={toggle} className="text-end chain-total-col">
										{cellMode === 'per_100' ? t('chain_network') : t('total')}
									</SortTh>
									{cellMode !== 'cost' && (
										<SortTh sortKey="cost" sort={sort} onSort={toggle} className="text-end">{t('cost_price')}</SortTh>
									)}
									<SortTh sortKey="share" sort={sort} onSort={toggle} className="text-end">{t('share')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={branches.length + (cellMode !== 'cost' ? 5 : 4)} />}
								{sorted.map((row, rowIndex) => {
									const values = branches.map(branch => cellValue(row, branch.pos_id))
									const rowMax = Math.max(0, ...values.map(value => Number(value || 0)))
									// Норма на 100 чеков сравнивается по строке: кто тратит больше на тот же поток гостей
									const normStats = relativeStats(columnStats(branches.map(branch => row.cells?.[branch.pos_id]).filter(cell => cell && cell.per_100 !== null), 'per_100'))
									const share = totals.cost > 0 ? row.cost / totals.cost * 100 : 0
									return (
										<tr key={row.product_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<ProductNameCell row={row} t={t} />
											{branches.map(branch => {
												const cell = row.cells?.[branch.pos_id]
												const value = values[branches.indexOf(branch)]
												const style = cell && cellMode === 'per_100'
													? heatStyle(cell.per_100, normStats, false)
													: intensityStyle(value, rowMax, 'success')
												return (
													<td key={branch.pos_id}
														className={`chain-num ${cell ? 'chain-click' : ''}`}
														style={cell ? style : undefined}
														title={cell ? `${branch.pos_name}: ${qtyUnit(cell.quantity, row.unit_name)} · ${money(cell.cost)} · ${qty(cell.per_100)} ${t('chain_cons_per_100_lower')}` : undefined}
														onClick={() => cell && openDetails(row, branch.pos_id)}>
														{formatCell(value)}
													</td>
												)
											})}
											<td className="chain-num chain-total-col">{formatCell(totalValue(row))}</td>
											{cellMode !== 'cost' && <td className="chain-num">{money(row.cost)}</td>}
											<FillCell percent={maxRowCost > 0 ? row.cost / maxRowCost * 100 : 0} tone="danger">{percent(share)}</FillCell>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tbody style={FOOT_EXTRA_STYLE}>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('cheques')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num">{formatMoney(branch.cheques)}</td>
										))}
										<td className="chain-num chain-total-col">{formatMoney(totals.cheques)}</td>
										{cellMode !== 'cost' && <td></td>}
										<td></td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('chain_cons_cost_per_cheque')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={branch.cheques > 0 ? heatStyle(branch.cost_per_cheque, branchStats.cost_per_cheque, false) : undefined}>
												{branch.cost_per_cheque !== null ? money(branch.cost_per_cheque) : '—'}
											</td>
										))}
										<td className="chain-num chain-total-col">{totals.cost_per_cheque !== null && totals.cost_per_cheque !== undefined ? money(totals.cost_per_cheque) : '—'}</td>
										{cellMode !== 'cost' && <td></td>}
										<td></td>
									</tr>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2" title={t('chain_cons_percent_hint')}>{t('chain_percent_of_revenue')}</td>
										{branches.map(branch => (
											<td key={branch.pos_id} className="chain-num" style={branch.cheques > 0 ? heatStyle(branch.cost_percent, branchStats.cost_percent, false) : undefined}
												title={`${t('revenue')}: ${money(branch.revenue)}`}>
												{percent(branch.cost_percent)}
											</td>
										))}
										<td className="chain-num chain-total-col">{percent(totals.cost_percent)}</td>
										{cellMode !== 'cost' && <td></td>}
										<td></td>
									</tr>
								</tbody>
							)}
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('cost_price')}</td>
										{branches.map(branch => (
											<FillCell key={branch.pos_id} percent={maxBranchCost > 0 ? branch.cost / maxBranchCost * 100 : 0} tone="danger">
												{money(branch.cost)}
												<div className="chain-muted">{percent(branch.share)}</div>
											</FillCell>
										))}
										<td className="chain-num chain-total-col">{money(totals.cost)}</td>
										{cellMode !== 'cost' && <td></td>}
										<td className="chain-num">100%</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_cons_matrix_hint')}
					</div>
				</div>
			</div>

			<CostPerChequeChart branches={branches} />

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.row.product_name} — {details?.pos_name || branchById[details?.pos_id]?.pos_name}
						<div className="chain-muted fw-normal">
							{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.cheques || 0)} {t('chain_cons_cheques_lower')} · {qtyUnit(details?.quantity, details?.unit_name)} · {money(details?.cost)}
							{details?.per_cheque !== null && details?.per_cheque !== undefined && <> · {qtyUnit(details.per_cheque, details.unit_name)} {t('chain_cons_per_cheque_lower')}</>}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<table className="chain-table">
						<thead>
							<tr>
								<th>#</th>
								<th>{t('date')}</th>
								<th>{t('cheque')}</th>
								<th>{t('order_type')}</th>
								<th className="text-end">{t('chain_cons_cheque_amount')}</th>
								<th className="text-end">{t('quantity')}</th>
								<th className="text-end">{t('cost_price')}</th>
							</tr>
						</thead>
						<tbody>
							{(details?.rows || []).length === 0 && <ChainEmpty colSpan={7} />}
							{(details?.rows || []).map((item, index) => (
								<tr key={item.id} style={item.kind === 'return' ? intensityStyle(0.2, 1, 'danger') : undefined}>
									<td className="chain-index">{index + 1}</td>
									<td className="text-nowrap">{formatDate(item.moved_at, 'dd.MM.yy HH:mm')}</td>
									<td>
										{item.cheque_number ? `№ ${item.cheque_number}` : (item.cheque_id ? `#${item.cheque_id}` : '—')}
										{kindLabel(item) && <div className="chain-muted">{kindLabel(item)}</div>}
									</td>
									<td>{orderTypeLabels[item.order_type_id] || '—'}</td>
									<td className="chain-num">{item.cheque_amount !== null ? money(item.cheque_amount) : '—'}</td>
									<td className="chain-num"><b>{qty(item.quantity)}</b></td>
									<td className="chain-num">{money(item.cost)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Себестоимость расхода на один чек по филиалам — кто тратит больше на того же гостя
function CostPerChequeChart({ branches }) {
	const { t } = useTranslation()
	const rows = branches.filter(branch => branch.cheques > 0).sort((a, b) => b.cost_per_cheque - a.cost_per_cheque)
	if (rows.length < 2) return null

	const series = [{ name: t('chain_cons_cost_per_cheque'), data: rows.map(branch => Math.round(branch.cost_per_cheque || 0)) }]
	const options = {
		chart: { type: 'bar', toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%', distributed: true } },
		colors: rows.map(branch => chainColor(branches.indexOf(branch))),
		dataLabels: { enabled: false },
		legend: { show: false },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: rows.map(branch => branch.pos_name), labels: { formatter: value => formatMoney(Math.round(value)) } },
		tooltip: {
			y: {
				formatter: (value, { dataPointIndex }) => `${formatMoney(Math.round(value))} · ${percent(rows[dataPointIndex]?.cost_percent)} ${t('chain_of_revenue')}`,
			},
		},
	}

	return (
		<div className="card chain-card mb-3">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_cons_cost_per_cheque_by_branches')}</h5></div>
				<ReactApexChart type="bar" height={Math.max(240, rows.length * 38 + 80)} series={series} options={options} />
			</div>
		</div>
	)
}

export default ElementSales
